import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCompose } from '@/contexts/ComposeContext';
import { Trash2, Paperclip, FileText, Loader2, X } from 'lucide-react';
import api, { handleApiError, starEmail, deleteEmail, searchEmailAddresses } from '@/services/api';
import LoadingState from '@/components/ui/LoadingState';
//import DOMPurify from 'dompurify';
import { authService } from '@/services/auth';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Tag } from 'react-tag-autocomplete';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-hot-toast';
import { useFolderStore } from '@/store/folderStore';
import EmailViewPanel from './EmailViewPanel';
import { useEmailReplies } from '@/hooks/useEmailReplies';
import { useDebounce } from '@/hooks/useDebounce';
import { finalizeDetailBody, getRenderableBody, hasUsableBody } from '@/utils/emailBody';
import {
  MAX_RECIPIENTS,
  mergeRecipientsIntoTags,
  shouldParseBulkPaste,
} from '@/utils/recipientParser';
import RecipientTagList from '@/components/email/RecipientTagList';
import type { PreviewableAttachment } from '@/utils/attachmentPreview';

interface Attachment { 
  id: string;
  filename: string;
  content_type: string;
  size: number;
  content?: string;
  public_url?: string;
}

interface EmailDetail {
  id: string;
  uid: number;
  seqno: number;
  flags: string[];
  size: number;
  envelope: {
    date: string;
    subject: string;
    from: Array<{ name: string; mailbox: string; host: string }>;
    sender: Array<{ name: string; mailbox: string; host: string }>;
    replyTo: Array<{ name: string; mailbox: string; host: string }>;
    to: Array<{ name: string | null; mailbox: string; host: string }>;
    cc: Array<{ name: string; mailbox: string; host: string }> | null;
    bcc: Array<{ name: string; mailbox: string; host: string }> | null;
    inReplyTo: string | null;
    messageId: string;
  };
  header: any;
  body: {
    html: string;
    text: string;
    raw: string;
    isPreview?: boolean;
    hasHtml?: boolean;
  };
  attachments: Attachment[];
  replyCount?: number;
}

type EmailNavigationState = {
  emailInitial?: EmailDetail;
  knownReplyCount?: number;
  listBodyFallback?: EmailDetail['body'];
  latestReplyFallback?: {
    html?: string;
    text?: string;
    raw?: string;
  };
};

// Add sound utility
const playSentSound = () => {
  const audio = new Audio('/sent.mp3');
  audio.play().catch(error => {
    console.error('Error playing sound:', error);
  });
};

// Quill modules configuration
const modules = {
  toolbar: [
    [{ 'header': [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
    ['link'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

// Quill formats
const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link'
];

const EmailChatPage: React.FC = () => {
  const location = useLocation();
  const {
    emailInitial,
    knownReplyCount: knownReplyCountFromNav,
    listBodyFallback,
    latestReplyFallback,
  } = (location.state as EmailNavigationState) || {};
  const knownReplyCount = knownReplyCountFromNav ?? emailInitial?.replyCount ?? 0;
  const { folderPath, id, uid } = useParams<{ folderPath: string; id: string; uid: string }>();
  const navigate = useNavigate();
  
  // All refs first
  const quillRef = useRef<ReactQuill>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const forwardFileInputRef = useRef<HTMLInputElement>(null);
  // List/nav reply count — detail API often returns 0 and must not wipe this
  const knownReplyCountRef = useRef(knownReplyCount);
  const bodyFallbackRef = useRef(listBodyFallback || emailInitial?.body || null);
  const latestReplyFallbackRef = useRef(latestReplyFallback || null);
  
  // Then all state hooks
  const [email, setEmail] = useState<EmailDetail | null>(
    emailInitial
      ? { ...emailInitial, replyCount: emailInitial.replyCount || knownReplyCount || 0 }
      : null
  );
  const [isStarred, setIsStarred] = useState(emailInitial?.flags?.includes('\\Flagged') || false);
  const [showReply, setShowReply] = useState(false);
  const [replyAll, setReplyAll] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [toTags, setToTags] = useState<Tag[]>([]);
  const [ccTags, setCcTags] = useState<Tag[]>([]);
  const [bccTags, setBccTags] = useState<Tag[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Forward state
  const [showForward, setShowForward] = useState(false);
  const [forwardSubject, setForwardSubject] = useState('');
  const [forwardBody, setForwardBody] = useState('');
  const [forwardToTags, setForwardToTags] = useState<Tag[]>([]);
  const [forwardCcTags, setForwardCcTags] = useState<Tag[]>([]);
  const [forwardBccTags, setForwardBccTags] = useState<Tag[]>([]);
  const [expandedRecipientFields, setExpandedRecipientFields] = useState<Record<string, boolean>>({});
  const [showForwardCc, setShowForwardCc] = useState(false);
  const [showForwardBcc, setShowForwardBcc] = useState(false);
  const [forwardAttachments, setForwardAttachments] = useState<File[]>([]);
  const [isForwarding] = useState(false);

  // Attachment state
  const [attachments, setAttachments] = useState<File[]>([]);

  // Loading state for send
  const [isSending] = useState(false);

  const { decrementUnreadCount } = useFolderStore();
  const hasMarkedAsReadRef = useRef(false);

  // Prefer email.replyCount, then list navigation count — detail API often returns 0
  const effectiveReplyCount =
    (email?.replyCount && email.replyCount > 0 ? email.replyCount : 0) ||
    knownReplyCountRef.current ||
    knownReplyCount ||
    0;

  // Always fetch replies when we have route ids; do not gate on replyCount (detail API zeros it)
  const { replies, loading: repliesLoading, hasMore, loadMore, refresh: refreshReplies } = useEmailReplies(
    id || '',
    uid || '',
    refreshKey
  );

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Email search state
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [activeInput, setActiveInput] = useState<'to' | 'cc' | 'bcc' | 'forwardTo' | 'forwardCc' | 'forwardBcc' | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Add a function to detect fold screen
  const isFoldScreen = () => {
    return window.innerWidth <= 280; // Typical fold screen width when folded
  };

  // Add state for fold screen
  const [, setIsFold] = useState(isFoldScreen());

  // Add effect to handle screen resize
  useEffect(() => {
    const handleResize = () => {
      setIsFold(isFoldScreen());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Fetch email suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (debouncedSearchQuery.length > 0) {
        try {
          const results = await searchEmailAddresses(debouncedSearchQuery);
          setSuggestions(results);
          setShowSuggestions(true);
          setActiveSuggestionIndex(-1);
        } catch (error) {
          console.error('Error fetching email suggestions:', error);
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    fetchSuggestions();
  }, [debouncedSearchQuery]);

  // Handle suggestion selection
  const handleSelectSuggestion = (email: string) => {
    const newTag = {
      label: email,
      value: email
    };

    switch (activeInput) {
      case 'to':
        setToTags([...toTags, newTag]);
        break;
      case 'cc':
        setCcTags([...ccTags, newTag]);
        break;
      case 'bcc':
        setBccTags([...bccTags, newTag]);
        break;
      case 'forwardTo':
        setForwardToTags([...forwardToTags, newTag]);
        break;
      case 'forwardCc':
        setForwardCcTags([...forwardCcTags, newTag]);
        break;
      case 'forwardBcc':
        setForwardBccTags([...forwardBccTags, newTag]);
        break;
    }

    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, inputType: 'to' | 'cc' | 'bcc' | 'forwardTo' | 'forwardCc' | 'forwardBcc') => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (activeSuggestionIndex >= 0) {
          handleSelectSuggestion(suggestions[activeSuggestionIndex]);
          if (e.currentTarget) {
            e.currentTarget.value = '';
          }
        } else if (e.currentTarget.value.trim()) {
          if (handleAddEmailTag(e.currentTarget.value, inputType)) {
            e.currentTarget.value = '';
            setSearchQuery('');
          }
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    } else if (e.key === 'Enter' && e.currentTarget.value.trim()) {
      e.preventDefault();
      if (handleAddEmailTag(e.currentTarget.value, inputType)) {
        e.currentTarget.value = '';
        setSearchQuery('');
      }
    }
  };

  const getRecipientTagsState = (
    inputType: 'to' | 'cc' | 'bcc' | 'forwardTo' | 'forwardCc' | 'forwardBcc',
  ) => {
    switch (inputType) {
      case 'to':
        return { tags: toTags, setTags: setToTags, otherCounts: [ccTags.length, bccTags.length] };
      case 'cc':
        return { tags: ccTags, setTags: setCcTags, otherCounts: [toTags.length, bccTags.length] };
      case 'bcc':
        return { tags: bccTags, setTags: setBccTags, otherCounts: [toTags.length, ccTags.length] };
      case 'forwardTo':
        return { tags: forwardToTags, setTags: setForwardToTags, otherCounts: [forwardCcTags.length, forwardBccTags.length] };
      case 'forwardCc':
        return { tags: forwardCcTags, setTags: setForwardCcTags, otherCounts: [forwardToTags.length, forwardBccTags.length] };
      case 'forwardBcc':
        return { tags: forwardBccTags, setTags: setForwardBccTags, otherCounts: [forwardToTags.length, forwardCcTags.length] };
      default:
        return { tags: [], setTags: () => undefined, otherCounts: [0, 0] };
    }
  };

  const handleAddEmailTag = (
    value: string,
    inputType: 'to' | 'cc' | 'bcc' | 'forwardTo' | 'forwardCc' | 'forwardBcc',
  ) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return false;

    const { tags, setTags, otherCounts } = getRecipientTagsState(inputType);
    const remainingSlots = MAX_RECIPIENTS - (otherCounts.reduce((sum, count) => sum + count, 0) + tags.length);
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_RECIPIENTS} recipients allowed per email`);
      return false;
    }

    const result = mergeRecipientsIntoTags(tags, trimmedValue, remainingSlots);
    if (result.added === 0 && result.invalid.length === 0 && result.duplicates > 0) {
      toast.error('All pasted addresses are already added');
      return false;
    }

    if (result.added > 0) {
      setTags(result.tags);
      if (result.added > 1) {
        toast.success(`Added ${result.added} recipients`);
      }
    }

    if (result.invalid.length > 0) {
      toast.error(`Skipped ${result.invalid.length} invalid address(es)`);
    }

    if (result.duplicates > 0 && result.added > 0) {
      toast(`${result.duplicates} duplicate address(es) skipped`, { icon: 'ℹ️' });
    }

    if (result.limitReached) {
      toast.error(`Maximum ${MAX_RECIPIENTS} recipients allowed per email`);
    }

    return result.added > 0;
  };

  const handleRecipientPaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    inputType: 'to' | 'cc' | 'bcc' | 'forwardTo' | 'forwardCc' | 'forwardBcc',
  ) => {
    const pasted = event.clipboardData.getData('text');
    if (!shouldParseBulkPaste(pasted)) return;

    event.preventDefault();
    handleAddEmailTag(pasted, inputType);
    event.currentTarget.value = '';
    setSearchQuery('');
  };

  const renderRecipientTags = (
    fieldKey: string,
    tags: Tag[],
    setTags: React.Dispatch<React.SetStateAction<Tag[]>>,
  ) => (
    <RecipientTagList
      tags={tags}
      expanded={!!expandedRecipientFields[fieldKey]}
      onToggleExpand={() => setExpandedRecipientFields((prev) => ({
        ...prev,
        [fieldKey]: !prev[fieldKey],
      }))}
      onRemove={(index) => setTags(tags.filter((_, idx) => idx !== index))}
      onClearAll={() => {
        setTags([]);
        setExpandedRecipientFields((prev) => ({ ...prev, [fieldKey]: false }));
      }}
    />
  );

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMobileMenu && !(event.target as Element).closest('.mobile-menu')) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMobileMenu]);

  const fetchAttachmentBlob = async (
    attachment: PreviewableAttachment,
    onProgress?: (progress: number) => void
  ): Promise<Blob> => {
    if (attachment.public_url) {
      const response = await fetch(attachment.public_url);
      if (!response.ok) {
        throw new Error('Failed to load attachment');
      }
      return response.blob();
    }

    if (!attachment.id) {
      throw new Error('This attachment can only be downloaded from the original message');
    }

    const response = await api.get(`/api/email/attachments/${attachment.id}/download`, {
      responseType: 'blob',
      onDownloadProgress: (progressEvent: { loaded: number; total?: number }) => {
        if (!onProgress) return;
        if (progressEvent.total) {
          onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        } else {
          onProgress(progressEvent.loaded);
        }
      },
    });

    return response.data;
  };

  const handleAttachmentDownload = async (attachment: PreviewableAttachment, onProgress: (progress: number) => void) => {
    try {
      const blob = await fetchAttachmentBlob(attachment, onProgress);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  // Custom CSS for ReactQuill styling
  const quillStyles = `
    .ql-container {
      border: none !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      height: auto !important;
      min-height: 200px;
    }
    
    .ql-editor {
      min-height: 200px;
      padding: 16px !important;
      border: none !important;
      outline: none !important;
      overflow-y: auto !important;
      height: ${isMobile ? 'calc(100vh - 400px)' : '300px'} !important;
    }
    
    .ql-toolbar {
      border: none !important;
      border-bottom: 1px solid #e5e7eb !important;
      background-color: #f9fafb;
      padding: 8px 16px !important;
      border-radius: 8px 8px 0 0;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    .ql-toolbar button {
      border-radius: 4px !important;
      margin: 2px !important;
    }
    
    .ql-toolbar button:hover {
      background-color: #e5e7eb !important;
    }
    
    .ql-toolbar .ql-active {
      background-color: #dbeafe !important;
      color: #1e40af !important;
    }
    
    .ql-container.ql-snow {
      border: none !important;
      border-radius: 0 0 8px 8px;
    }
    
    .ql-toolbar.ql-snow {
      border: none !important;
    }
  `;

  // Tiptap editor for reply body
  const replyEditor = useEditor({
    extensions: [StarterKit],
    content: replyBody,
    onUpdate: ({ editor }) => {
      setReplyBody(editor.getHTML());
    },
  });

  // Update editor content when replyBody changes
  useEffect(() => {
    if (replyEditor && replyBody !== replyEditor.getHTML()) {
      replyEditor.commands.setContent(replyBody);
    }
  }, [replyBody, replyEditor]);

  // Inject custom ReactQuill styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = quillStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [quillStyles]);

  // Use initial data and fetch latest version
  useEffect(() => {
    const fetchEmail = async () => {
      if (!folderPath || !id || !uid) {
      // if (!folderPath || !id) {
        return;
      }

      try {
        const response = await api.get<EmailDetail>(`/api/folders/${folderPath}/emails/${id}/${uid}`);
        // const response = await api.get<EmailDetail>(`/api/folders/${folderPath}/emails/${id}`);
        // Detail payload often returns replyCount: 0 — keep the list/nav value
        const preservedReplyCount = Math.max(
          response.data.replyCount || 0,
          knownReplyCountRef.current || 0,
          knownReplyCount || 0,
          emailInitial?.replyCount || 0
        );
        if (preservedReplyCount > 0) {
          knownReplyCountRef.current = preservedReplyCount;
        }

        // Detail/replies APIs sometimes return body "0" — keep list/initial content.
        // Clear isPreview after detail so bounce/DSN text bodies are not stuck loading.
        const mergedBody = finalizeDetailBody(
          response.data.body,
          bodyFallbackRef.current || emailInitial?.body
        );
        if (hasUsableBody(mergedBody)) {
          bodyFallbackRef.current = mergedBody;
        }

        setEmail({
          ...response.data,
          replyCount: preservedReplyCount,
          body: mergedBody as EmailDetail['body'],
          flags: Array.isArray(response.data.flags) ? response.data.flags : [],
          envelope: {
            ...response.data.envelope,
            from: response.data.envelope?.from?.length
              ? response.data.envelope.from
              : [{ name: 'Mailer Daemon', mailbox: 'mailer-daemon', host: 'localhost' }],
            to: response.data.envelope?.to || [],
          },
        });
        setIsStarred(Array.isArray(response.data.flags) && response.data.flags.includes('\\Flagged'));

        // If this is a draft, open it in the compose modal
        if (folderPath === 'Drafts') {
          const toTags = response.data.envelope.to.map(recipient => ({
            value: `${recipient.mailbox}@${recipient.host}`,
            label: recipient.name || `${recipient.mailbox}@${recipient.host}`
          }));

          const ccTags = response.data.envelope.cc?.map(recipient => ({
            value: `${recipient.mailbox}@${recipient.host}`,
            label: recipient.name || `${recipient.mailbox}@${recipient.host}`
          })) || [];

          const bccTags = response.data.envelope.bcc?.map(recipient => ({
            value: `${recipient.mailbox}@${recipient.host}`,
            label: recipient.name || `${recipient.mailbox}@${recipient.host}`
          })) || [];

          // Convert attachments to File objects if they exist
          let attachments: File[] = [];
          if (response.data.attachments && response.data.attachments.length > 0) {
            try {
              attachments = await Promise.all(response.data.attachments.map(async (attachment) => {
                const attachmentResponse = await api.get(`/api/email/attachment/${id}/${attachment.id}`, {
                  responseType: 'blob'
                });
                return new File([attachmentResponse.data], attachment.filename, {
                  type: attachment.content_type
                });
              }));
            } catch (error) {
              console.error('Error fetching attachments:', error);
              toast.error('Failed to load some attachments');
            }
          }

          openCompose({
            type: 'draft', // Add type to indicate this is a draft
            to: toTags,
            cc: ccTags,
            bcc: bccTags,
            subject: response.data.envelope.subject || '',
            body: getRenderableBody(response.data.body).html,
            attachments: attachments,
            draftId: id // Pass the draft ID to update it instead of creating a new one
          });

          // Navigate back to the folder
          navigate(`/folder/${folderPath}`);
        }
      } catch (err) {
        const errorMessage = handleApiError(err);
        console.error('Error fetching email:', errorMessage);
        setLoadError(errorMessage || 'Failed to load email');
        toast.error(errorMessage || 'Failed to load email');
      }
    };

    setLoadError(null);
    fetchEmail();
  }, [folderPath, uid, id, emailInitial]);

  // Mark email as read when loaded
  useEffect(() => {
    // console.log('Read effect triggered with:', {
    //   emailExists: !!email,
    //   folderPath,
    //   uid,
    //   id,
    //   flags: email?.flags,
    //   isAlreadyRead: email?.flags?.includes('\\Seen'),
    //   hasMarkedAsRead: hasMarkedAsReadRef.current
    // });

    if (!email || !folderPath || !uid || !id) return;
    if (folderPath === 'Sent') return; // Don't mark sent emails as read
    if (email.flags.includes('\\Seen')) return; // Already read
    if (hasMarkedAsReadRef.current) return; // Don't mark as read twice

    // console.log('Marking email as read...');
    hasMarkedAsReadRef.current = true;

    // Mark as read via API 
    api.put(`/api/email/message/${folderPath}/${id}/${uid}/read`).then(() => {
      // Update local state
      setEmail(prev => prev ? { ...prev, flags: [...prev.flags, '\\Seen'] } : prev);

      // Decrement unread count in folder store
      decrementUnreadCount(folderPath);

      // Update localStorage cache for this email in the folder/page
      // Try to update all possible pages (for infinite scroll)
      for (let page = 1; page <= 10; page++) { // Check first 10 pages
        const localKey = `emails_${folderPath}_${page}`;
        const cached = localStorage.getItem(localKey);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            let updated = false;
            const updatedEmails = (parsed.emails || []).map((msg: any) => {
              if (msg.uid === email.uid && !msg.flags.includes('\\Seen')) {
                updated = true;
                return { ...msg, flags: [...msg.flags, '\\Seen'] };
              }
              return msg;
            });
            if (updated) {
              localStorage.setItem(localKey, JSON.stringify({ ...parsed, emails: updatedEmails }));
            }
          } catch { }
        }
      }
    }).catch(() => {
      hasMarkedAsReadRef.current = false; // Reset on error
      // toast.error('Failed to mark email as read');
    });
  }, [email, folderPath, uid, id, decrementUnreadCount]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#f59e0b', '#059669', '#dc2626', '#4f46e5', '#7c3aed', '#0891b2'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const handleBack = () => {
    // Remove console.log
    navigate(-1);
    // navigate(`/${folderPath}`);
  };

  const handleReply = () => {
    if (!email) return;

    const fromAddress = email.envelope.from[0];
    const replyToAddress = email.envelope.replyTo?.[0] || fromAddress;

    // If we're in the Sent folder, use the original recipient (TO) instead of the sender
    const toEmail = folderPath === 'Sent'
      ? `${email.envelope.to[0].mailbox}@${email.envelope.to[0].host}`
      : `${replyToAddress.mailbox}@${replyToAddress.host}`;

    // Fix subject handling
    const subject = email.envelope.subject || '';
    const replyPrefix = 'Re: ';
    const normalizedSubject = subject.trim();
    const finalSubject = normalizedSubject.startsWith(replyPrefix) 
      ? normalizedSubject 
      : `${replyPrefix}${normalizedSubject}`;

    // Get active signature
    const storedSignature = localStorage.getItem('activeSignature');
    let signatureHtml = '';
    if (storedSignature) {
      try {
        const activeSignature = JSON.parse(storedSignature);
        signatureHtml = activeSignature.signature;
      } catch (error) {
        // Remove console.error
      }
    }

    // Create reply body with signature at the top
    const replyBodyHtml = `
      ${signatureHtml}<br><br>
      <hr>
      <br>
      On ${new Date(email.envelope.date).toLocaleString()}, ${fromAddress.name || `${fromAddress.mailbox}@${fromAddress.host}`} wrote:
      <br>
      <blockquote style="border-left: 2px solid #ccc; margin: 10px 0; padding-left: 10px;"> 
        ${getRenderableBody(email.body).html}
      </blockquote>
    `;

    setReplySubject(finalSubject);
    setReplyBody(replyBodyHtml);
    setToTags([{ value: toEmail, label: toEmail }]);
    setCcTags([]);
    setShowReply(true);
    setReplyAll(false);
  };

  const handleReplyAll = () => {
    if (!email) return;

    const fromAddress = email.envelope.from[0];
    const replyToAddress = email.envelope.replyTo?.[0] || fromAddress;
    const currentUserEmail = authService.getCurrentUser()?.email;

    // Fix subject handling
    const subject = email.envelope.subject || '';
    const replyPrefix = 'Re: ';
    const normalizedSubject = subject.trim();
    const finalSubject = normalizedSubject.startsWith(replyPrefix) 
      ? normalizedSubject 
      : `${replyPrefix}${normalizedSubject}`;

    // Get active signature
    const storedSignature = localStorage.getItem('activeSignature');
    let signatureHtml = '';
    if (storedSignature) {
      try {
        const activeSignature = JSON.parse(storedSignature);
        // Clean up the signature HTML to ensure proper rendering
        signatureHtml = activeSignature.signature
          .replace(/<p><br><\/p>/g, '<br>')
          .replace(/<p>(.*?)<\/p>/g, '$1<br>')
          .replace(/(<br>)+$/, '')
          .trim();
      } catch (error) {
        // Remove console.error
      }
    }

    // Create reply body with signature at the top
    const replyBodyHtml = `
      <div class="signature-container" style="margin-bottom: 20px; font-family: inherit;">
        ${signatureHtml}
      </div>
      <hr>
      <br>
      On ${new Date(email.envelope.date).toLocaleString()}, ${fromAddress.name || `${fromAddress.mailbox}@${fromAddress.host}`} wrote:
      <br>
      <blockquote style="border-left: 2px solid #ccc; margin: 10px 0; padding-left: 10px;">
        ${getRenderableBody(email.body).html}
      </blockquote>
    `;

    // Collect all recipients for Reply All
    const toRecipients = new Set<Tag>();
    const ccRecipients = new Set<Tag>();

    if (folderPath === 'Sent') {
      // If we're in the Sent folder:
      // Add original TO recipients
      email.envelope.to?.forEach(recipient => {
        const emailAddress = `${recipient.mailbox}@${recipient.host}`;
        toRecipients.add({ value: emailAddress, label: emailAddress });
      });
    } else {
      // If we're not in the Sent folder:
      // 1. Add the original sender to TO
      const senderEmail = `${replyToAddress.mailbox}@${replyToAddress.host}`;
      toRecipients.add({ value: senderEmail, label: senderEmail });

      // 2. Add original TO recipients except ourselves
      email.envelope.to?.forEach(recipient => {
        const emailAddress = `${recipient.mailbox}@${recipient.host}`;
        if (emailAddress !== currentUserEmail) {
          toRecipients.add({ value: emailAddress, label: emailAddress });
        }
      });
    }

    // Add CC recipients if they exist (except ourselves)
    email.envelope.cc?.forEach(recipient => {
      const emailAddress = `${recipient.mailbox}@${recipient.host}`;
      if (emailAddress !== currentUserEmail) {
        ccRecipients.add({ value: emailAddress, label: emailAddress });
      }
    });

    setReplySubject(finalSubject);
    setReplyBody(replyBodyHtml);
    setToTags(Array.from(toRecipients));
    setCcTags(Array.from(ccRecipients));
    setBccTags([]);
    setShowReply(true);
    setReplyAll(true);
    if (ccRecipients.size > 0) {
      setShowCc(true);
    }
  };

  // Handle cancel reply
  const handleCancelReply = () => {
    // Reset all reply-related states
    setShowReply(false);
    setReplyAll(false);
    setReplySubject('');
    setReplyBody('');
    setToTags([]);
    setCcTags([]);
    setBccTags([]);
    setShowCc(false);
    setShowBcc(false);
    setAttachments([]);
    setSearchQuery('');
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    setActiveInput(null);
  };

  const handleSendReply = async () => {
    if (!email || !folderPath || !uid) return;

    // Close the reply form immediately
    handleCancelReply();

    try {
      // Show sending toast
      const loadingToast = toast.loading('Sending reply...', {
        position: 'bottom-center',
        duration: Infinity,
        style: {
          background: "#000",
          color: "#fff",
        }
      });

      const formData = new FormData();

      // Common fields
      formData.append('originalFolderPath', folderPath);
      formData.append('originalUid', uid);
      formData.append('subject', replySubject);
      formData.append('body', replyBody);
      formData.append('bodyType', 'html');

      // Set reply type based on replyAll state
      if (replyAll) {
        formData.append('replyType', 'replyAll');
        formData.append('includeOriginalAttachments', 'true');
      } else {
        formData.append('replyType', 'reply');
      }

      // Add recipients
      toTags.forEach(tag => {
        if (tag.value) {
          formData.append('to', String(tag.value));
        }
      });

      ccTags.forEach(tag => {
        if (tag.value) {
          formData.append('cc', String(tag.value));
        }
      });

      bccTags.forEach(tag => {
        if (tag.value) {
          formData.append('bcc', String(tag.value));
        }
      });

      // Add new attachments
      attachments.forEach(file => {
        formData.append('attachments', file);
      });

      // Send the reply
      const response = await api.post('/api/email/reply?implementation=node-imap', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 0
      });

      if (response.status === 200 && response.data.success) {
        // Play sent sound
        playSentSound();

        // Update toast to success
        toast.success('Reply sent successfully', {
          id: loadingToast,
          position: 'bottom-center',
          duration: 3000
        });

        // Reload email data
        const emailResponse = await api.get<EmailDetail>(`/api/folders/${folderPath}/emails/${id}/${uid}`);
        // const emailResponse = await api.get<EmailDetail>(`/api/folders/${folderPath}/emails/${id}`);
        const preservedReplyCount = Math.max(
          emailResponse.data.replyCount || 0,
          knownReplyCountRef.current || 0,
          1 // at least this reply we just sent
        );
        knownReplyCountRef.current = preservedReplyCount;
        const mergedBody = finalizeDetailBody(
          emailResponse.data.body,
          bodyFallbackRef.current
        );
        setEmail({
          ...emailResponse.data,
          replyCount: preservedReplyCount,
          body: mergedBody as EmailDetail['body'],
          flags: Array.isArray(emailResponse.data.flags) ? emailResponse.data.flags : [],
        });
        setIsStarred(
          Array.isArray(emailResponse.data.flags) &&
            emailResponse.data.flags.includes('\\Flagged')
        );

        // Reload replies after send (server needs a moment to index the new reply)
        setTimeout(() => {
          setRefreshKey(prev => prev + 1);
          refreshReplies();
        }, 5000);
      }
    } catch (error) {
      toast.error('Failed to send reply', {
        position: 'bottom-center',
        duration: 3000
      });
    }
  };

  // Handle file attachment
  // const handleFileAttach = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const files = Array.from(event.target.files || []);
  //   setAttachments(prev => [...prev, ...files]);
  //   // Reset input value to allow selecting the same file again
  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = '';
  //   }
  // };

  // Handle forward file attachment
  const handleForwardFileAttach = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setForwardAttachments(prev => [...prev, ...files]);
    // Reset input value to allow selecting the same file again
    if (forwardFileInputRef.current) {
      forwardFileInputRef.current.value = '';
    }
  };

  // Remove attachment
  // const removeAttachment = (index: number) => {
  //   setAttachments(prev => prev.filter((_, i) => i !== index));
  // };

  // Remove forward attachment
  const removeForwardAttachment = (index: number) => {
    setForwardAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleForward = () => {
    if (!email) return;
    setShowForward(true);

    // Set forward subject with "Fwd:" prefix
    setForwardSubject(`Fwd: ${email.envelope.subject || ''}`);

    // Set forward body with original email content
    const originalEmailContent = `
      <br><br>
      <hr>
      <p><strong>From:</strong> ${email.envelope.from?.[0]?.name || email.envelope.from?.[0]?.mailbox} &lt;${email.envelope.from?.[0]?.mailbox}@${email.envelope.from?.[0]?.host}&gt;</p>
      <p><strong>Date:</strong> ${formatDate(email.envelope.date)}</p>
      <p><strong>Subject:</strong> ${email.envelope.subject || ''}</p>
      <p><strong>To:</strong> ${(email.envelope.to || []).map(t => `${t.name || t.mailbox} &lt;${t.mailbox}@${t.host}&gt;`).join(', ')}</p>
      ${email.envelope.cc && email.envelope.cc.length > 0 ? `<p><strong>CC:</strong> ${email.envelope.cc.map(t => `${t.name || t.mailbox} &lt;${t.mailbox}@${t.host}&gt;`).join(', ')}</p>` : ''}
      <br>
      ${getRenderableBody(email.body).html}
    `;

    setForwardBody(originalEmailContent);
    setForwardToTags([]);
    setForwardCcTags([]);
    setForwardBccTags([]);
    setForwardAttachments([]);
  };

  // Handle cancel forward
  const handleCancelForward = () => {
    // Reset all forward-related states
    setShowForward(false);
    setForwardSubject('');
    setForwardBody('');
    setForwardToTags([]);
    setForwardCcTags([]);
    setForwardBccTags([]);
    setShowForwardCc(false);
    setShowForwardBcc(false);
    setForwardAttachments([]);
    setSearchQuery('');
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    setActiveInput(null);
  };

  const handleSendForward = async () => {
    if (!email || !folderPath || !uid) return;

    // Close the forward form immediately
    handleCancelForward();

    try {
      // Show sending toast
      const loadingToast = toast.loading('Sending forward...', {
        position: 'bottom-center',
        duration: Infinity,
        style: {
          background: "#000",
          color: "#fff",
        }
      });

      const formData = new FormData();

      // Add recipients
      forwardToTags.forEach(tag => {
        if (tag.value) {
          formData.append('to', String(tag.value));
        }
      });

      forwardCcTags.forEach(tag => {
        if (tag.value) {
          formData.append('cc', String(tag.value));
        }
      });

      forwardBccTags.forEach(tag => {
        if (tag.value) {
          formData.append('bcc', String(tag.value));
        }
      });

      // Add subject and body
      formData.append('subject', forwardSubject);
      formData.append('body', forwardBody);
      formData.append('includeOriginalAttachments', 'true');

      // Add new attachments
      forwardAttachments.forEach(file => {
        formData.append('attachments', file);
      });

      // Send the forward
      const response = await api.post(`/api/email/forward/${folderPath}/${uid}?implementation=node-imap`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 0 // Remove timeout
      });

      if (response.status === 200 && response.data.success) {
        // Play sent sound
        playSentSound();

        // Update toast to success
        toast.success('Email forwarded successfully', {
          id: loadingToast,
          position: 'bottom-center',
          duration: 3000
        });
      }
    } catch (error) {
      toast.error('Failed to forward email', {
        position: 'bottom-center',
        duration: 3000
      });
    }
  };

  const handleDelete = async () => {
    if (!email || !folderPath || !email.id || !email.uid) return;

    try {
      await deleteEmail(folderPath, email.id, email.uid);
      // Navigate back to the folder view
      navigate(`/${folderPath}`);
    } catch (err: any) {
      toast.error('Failed to delete email: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleStar = async () => {
    if (!folderPath || !uid || !id) return;
    const newStarred = !isStarred;
    setIsStarred(newStarred);
    try {
      await starEmail(folderPath, uid, id, newStarred);
    } catch (err) {
      setIsStarred(!newStarred); // revert
    }
  };

  const [showMoreActions, setShowMoreActions] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isShowingOriginal, setIsShowingOriginal] = useState(false);
  const [isSendingCopy, setIsSendingCopy] = useState(false);
  const { openCompose } = useCompose();
  const downloadToastId = useRef<string>();
  const printToastId = useRef<string>();
  const viewToastId = useRef<string>();
  const originalToastId = useRef<string>();
  const copyToastId = useRef<string>();

  const handleSendCopy = async () => {
    if (!email || isSendingCopy) return;
    
    setIsSendingCopy(true);
    copyToastId.current = toast.loading('Preparing email copy...');

    try {
      // Get attachments if any
      const attachments: File[] = [];
      if (email.attachments?.length) {
        // Download each attachment
                  const downloadedFiles = await Promise.all(email.attachments.map(async (attachment) => {
            const response = await api.get(`/api/email/attachments/${attachment.id}/download`, {
              responseType: 'arraybuffer'
            });
            
            // Convert array buffer to blob with proper content type
            const blob = new Blob([response.data], { type: attachment.content_type });
            return new File([blob], attachment.filename, { type: attachment.content_type });
          }));
        attachments.push(...downloadedFiles);
      }

      // Open compose modal with email content
      openCompose({
        type: 'new',
        subject: `Copy: ${email.envelope.subject || 'No Subject'}`,
        body: getRenderableBody(email.body).html,
        attachments
      });

      if (copyToastId.current) {
        toast.success('Email copy ready to send', {
          id: copyToastId.current
        });
      }
    } catch (error) {
      console.error('Error preparing email copy:', error);
      if (copyToastId.current) {
        toast.error('Failed to prepare email copy. Please try again.', {
          id: copyToastId.current,
          duration: 4000
        });
      }
    } finally {
      setIsSendingCopy(false);
    }
  };

  const handleShowOriginal = async () => {
    if (!email?.id || isShowingOriginal) return;
    
    setIsShowingOriginal(true);
    originalToastId.current = toast.loading('Loading original email...');

    try {
      // Get the raw email content
      const response = await api.get(`/api/email/download/${email.id}`, {
        responseType: 'text'
      });

      // Create a new window for viewing
      const viewWindow = window.open('', '_blank');
      if (!viewWindow) {
        toast.error(
          <div style={{ maxWidth: '300px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
              Popup Blocked
            </div>
            <div style={{ fontSize: '14px' }}>
              To view the original email, please:
              <ol style={{ paddingLeft: '20px', marginTop: '4px' }}>
                <li>Allow popups for this site</li>
                <li>Look for the popup blocked icon in your browser's address bar</li>
                <li>Try viewing again</li>
              </ol>
            </div>
          </div>,
          {
            id: originalToastId.current,
            duration: 6000,
          }
        );
        throw new Error('POPUP_BLOCKED');
      }

      // Write the raw content with monospace formatting and headers section
      viewWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Original Email - ${email.envelope.subject || 'No Subject'}</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                line-height: 1.5;
                margin: 0;
                padding: 20px;
                background: #f8f9fa;
                color: #202124;
              }
              .container {
                max-width: 1000px;
                margin: 0 auto;
                background: white;
                border-radius: 8px;
                box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3);
                padding: 24px;
              }
              .page-header {
                margin-bottom: 24px;
                padding-bottom: 16px;
                border-bottom: 1px solid #dadce0;
              }
              .title {
                font-size: 20px;
                font-weight: 500;
                color: #202124;
                margin: 0;
              }
              .section {
                margin-bottom: 24px;
              }
              .section-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 12px;
                padding-bottom: 8px;
                border-bottom: 1px solid #dadce0;
              }
              .section-title {
                font-size: 16px;
                font-weight: 500;
                color: #202124;
                margin: 0;
              }
              .section-badge {
                background: #e8f0fe;
                color: #1967d2;
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 12px;
                font-weight: 500;
              }
              .content {
                font-family: 'Courier New', Courier, monospace;
                font-size: 13px;
                line-height: 1.5;
                white-space: pre-wrap;
                word-wrap: break-word;
                background: #f8f9fa;
                padding: 16px;
                border-radius: 4px;
                border: 1px solid #dadce0;
                margin: 0;
                overflow-x: auto;
              }
              .header-table {
                width: 100%;
                border-collapse: collapse;
                font-family: 'Courier New', Courier, monospace;
                font-size: 13px;
              }
              .header-table tr:nth-child(even) {
                background: #f8f9fa;
              }
              .header-table td {
                padding: 8px 12px;
                border: 1px solid #dadce0;
                vertical-align: top;
              }
              .header-table td:first-child {
                width: 180px;
                font-weight: 500;
                color: #1967d2;
                background: #f8f9fa;
              }
              .print-button {
                position: fixed;
                top: 20px;
                right: 20px;
                padding: 8px 24px;
                background: #1a73e8;
                color: white;
                border: none;
                border-radius: 4px;
                font-size: 14px;
                cursor: pointer;
                box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3);
              }
              .print-button:hover {
                background: #1557b0;
              }
              @media print {
                body {
                  background: white;
                  padding: 0;
                }
                .container {
                  box-shadow: none;
                }
                .print-button {
                  display: none;
                }
                .content, .header-table td {
                  border-color: #e0e0e0;
                }
                .section {
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <button onclick="window.print()" class="print-button">Print</button>
            <div class="container">
              <div class="page-header">
                <h1 class="title">Original Email Source</h1>
              </div>

              <!-- Email Headers Section -->
              <div class="section">
                <div class="section-header">
                  <h2 class="section-title">Email Headers</h2>
                  <span class="section-badge">Technical Details</span>
                </div>
                <table class="header-table">
                  <tr>
                    <td>Message-ID</td>
                    <td>${email.header.message_id || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td>Date</td>
                    <td>${new Date(email.envelope.date).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td>From</td>
                    <td>${email.envelope.from.map(f => `${f.name || ''} <${f.mailbox}@${f.host}>`).join(', ')}</td>
                  </tr>
                  <tr>
                    <td>To</td>
                    <td>${email.envelope.to.map(t => `${t.name || ''} <${t.mailbox}@${t.host}>`).join(', ')}</td>
                  </tr>
                  ${email.envelope.cc?.length ? `
                  <tr>
                    <td>Cc</td>
                    <td>${email.envelope.cc.map(c => `${c.name || ''} <${c.mailbox}@${c.host}>`).join(', ')}</td>
                  </tr>` : ''}
                  <tr>
                    <td>Subject</td>
                    <td>${email.envelope.subject || 'No Subject'}</td>
                  </tr>
                  ${Object.entries(email.header || {}).map(([key, value]) => `
                  <tr>
                    <td>${key}</td>
                    <td>${value}</td>
                  </tr>`).join('')}
                </table>
              </div>

              <!-- Raw Email Content Section -->
              <div class="section">
                <div class="section-header">
                  <h2 class="section-title">Raw Email Content</h2>
                  <span class="section-badge">RFC 822</span>
                </div>
                <pre class="content">${response.data}</pre>
              </div>
            </div>
          </body>
        </html>
      `);

      viewWindow.document.close();
      
      // Show success message
      if (originalToastId.current) {
        toast.success('Original email opened in new tab', {
          id: originalToastId.current
        });
      }

    } catch (error) {
      console.error('Error showing original email:', error);
      
      // Don't show another error message if we already showed the popup blocked message
      if (error instanceof Error && error.message === 'POPUP_BLOCKED') {
        return;
      }
      
      if (originalToastId.current) {
        toast.error('Failed to show original email. Please try again.', {
          id: originalToastId.current,
          duration: 4000
        });
      }
    } finally {
      setIsShowingOriginal(false);
    }
  };

  // Shared styles for print and view
  const emailStyles = `
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.5;
      color: #202124;
      margin: 0;
      padding: 0;
      background-color: #f8f9fa;
    }
    .container {
      max-width: 1000px;
      margin: 0 auto;
      padding: 20px;
    }
    .section {
      background: #fff;
      border-radius: 8px;
      box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3);
      margin-bottom: 20px;
    }
    .section-header {
      padding: 16px 24px;
      border-bottom: 1px solid #dadce0;
    }
    .section-title {
      font-size: 16px;
      font-weight: 500;
      color: #202124;
      margin: 0;
    }
    .header-content {
      padding: 20px 24px;
    }
    .subject {
      font-size: 22px;
      font-weight: 400;
      color: #202124;
      margin: 0 0 16px 0;
      padding-bottom: 12px;
      border-bottom: 1px solid #dadce0;
    }
    .metadata {
      color: #5f6368;
      font-size: 13px;
    }
    .metadata-row {
      display: flex;
      margin-bottom: 8px;
      align-items: baseline;
    }
    .metadata-label {
      color: #202124;
      font-weight: 500;
      width: 55px;
      flex-shrink: 0;
    }
    .metadata-value {
      margin-left: 8px;
      word-break: break-word;
    }
    .email-content {
      padding: 24px;
      font-size: 14px;
      line-height: 1.6;
    }
    .print-button {
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 8px 24px;
      background: #1a73e8;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
      box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3);
    }
    .print-button:hover {
      background: #1557b0;
    }
    @media print {
      body {
        background-color: #fff;
      }
      .container {
        padding: 0;
        page-break-inside: avoid;
        page-break-before: auto;
        page-break-after: auto;
      }
      .section {
        box-shadow: none;
        margin-bottom: 30px;
        break-inside: avoid;
      }
      .print-button {
        display: none;
      }
      /* Ensure sections stay together */
      .section + .section {
        margin-top: 20px;
        break-before: avoid;
      }
    }
  `;

  const handleViewHtml = async () => {
    if (!email?.id || isViewing) return;
    
    setIsViewing(true);
    viewToastId.current = toast.loading('Opening email view...');

    try {
      // Create a new window for viewing
      const viewWindow = window.open('', '_blank');
      if (!viewWindow) {
        // Show a more detailed error message with instructions
        toast.error(
          <div style={{ maxWidth: '300px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
              Popup Blocked
            </div>
            <div style={{ fontSize: '14px' }}>
              To view the email, please:
              <ol style={{ paddingLeft: '20px', marginTop: '4px' }}>
                <li>Allow popups for this site</li>
                <li>Look for the popup blocked icon in your browser's address bar</li>
                <li>Try viewing again</li>
              </ol>
            </div>
          </div>,
          {
            id: viewToastId.current,
            duration: 6000,
          }
        );
        throw new Error('POPUP_BLOCKED');
      }

      // Write the same HTML content but without the print button
      viewWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${email.envelope.subject || 'Email'}</title>
            <style>
              ${emailStyles}
              .print-button { display: none; }
            </style>
          </head>
          <body>
            <div class="container">
              <!-- Email Headers Section -->
              <div class="section">
                <div class="section-header">
                  <h2 class="section-title">Email Headers</h2>
                </div>
                <div class="header-content">
                  <h1 class="subject">${email.envelope.subject || 'No Subject'}</h1>
                  <div class="metadata">
                    <div class="metadata-row">
                      <span class="metadata-label">From:</span>
                      <span class="metadata-value">${email.envelope.from.map(f => f.name || `${f.mailbox}@${f.host}`).join(', ')}</span>
                    </div>
                    <div class="metadata-row">
                      <span class="metadata-label">To:</span>
                      <span class="metadata-value">${email.envelope.to.map(t => t.name || `${t.mailbox}@${t.host}`).join(', ')}</span>
                    </div>
                    ${email.envelope.cc?.length ? `
                    <div class="metadata-row">
                      <span class="metadata-label">Cc:</span>
                      <span class="metadata-value">${email.envelope.cc.map(c => c.name || `${c.mailbox}@${c.host}`).join(', ')}</span>
                    </div>` : ''}
                    <div class="metadata-row">
                      <span class="metadata-label">Date:</span>
                      <span class="metadata-value">${new Date(email.envelope.date).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Email Content Section -->
              <div class="section">
                <div class="section-header">
                  <h2 class="section-title">Email Content</h2>
                </div>
                <div class="email-content">
                  ${getRenderableBody(email.body).html || 'No content available'}
                </div>
              </div>
            </div>
          </body>
        </html>
      `);

      viewWindow.document.close();
      
      // Show success message
      if (viewToastId.current) {
        toast.success('Email opened in new tab', {
          id: viewToastId.current
        });
      }

    } catch (error) {
      console.error('Error opening email view:', error);
      
      // Don't show another error message if we already showed the popup blocked message
      if (error instanceof Error && error.message === 'POPUP_BLOCKED') {
        return;
      }
      
      if (viewToastId.current) {
        toast.error('Failed to open email view. Please try again.', {
          id: viewToastId.current,
          duration: 4000
        });
      }
    } finally {
      setIsViewing(false);
    }
  };

  const handlePrintEmail = async () => {
    if (!email?.id || isPrinting) return;
    
    setIsPrinting(true);
    printToastId.current = toast.loading('Preparing email for printing...');

    try {
      // No need to fetch raw content since we're using the HTML body

      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        // Show a more detailed error message with instructions
        toast.error(
          <div style={{ maxWidth: '300px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
              Popup Blocked
            </div>
            <div style={{ fontSize: '14px' }}>
              To print the email, please:
              <ol style={{ paddingLeft: '20px', marginTop: '4px' }}>
                <li>Allow popups for this site</li>
                <li>Look for the popup blocked icon in your browser's address bar</li>
                <li>Try printing again</li>
              </ol>
            </div>
          </div>,
          {
            id: printToastId.current,
            duration: 6000,
          }
        );
        throw new Error('POPUP_BLOCKED');
      }

      // Style and format the content for printing
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${email.envelope.subject || 'Email'}</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                line-height: 1.5;
                color: #202124;
                margin: 0;
                padding: 0;
                background-color: #f8f9fa;
              }
              .container {
                max-width: 1000px;
                margin: 0 auto;
                padding: 20px;
              }
              .section {
                background: #fff;
                border-radius: 8px;
                box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3);
                margin-bottom: 20px;
              }
              .section-header {
                padding: 16px 24px;
                border-bottom: 1px solid #dadce0;
              }
              .section-title {
                font-size: 16px;
                font-weight: 500;
                color: #202124;
                margin: 0;
              }
              .header-content {
                padding: 20px 24px;
              }
              .subject {
                font-size: 22px;
                font-weight: 400;
                color: #202124;
                margin: 0 0 16px 0;
                padding-bottom: 12px;
                border-bottom: 1px solid #dadce0;
              }
              .metadata {
                color: #5f6368;
                font-size: 13px;
              }
              .metadata-row {
                display: flex;
                margin-bottom: 8px;
                align-items: baseline;
              }
              .metadata-label {
                color: #202124;
                font-weight: 500;
                width: 55px;
                flex-shrink: 0;
              }
              .metadata-value {
                margin-left: 8px;
                word-break: break-word;
              }
              .email-content {
                padding: 24px;
                font-size: 14px;
                line-height: 1.6;
              }
              .print-button {
                position: fixed;
                top: 20px;
                right: 20px;
                padding: 8px 24px;
                background: #1a73e8;
                color: white;
                border: none;
                border-radius: 4px;
                font-size: 14px;
                cursor: pointer;
                box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3);
              }
              .print-button:hover {
                background: #1557b0;
              }
              @media print {
                body {
                  background-color: #fff;
                }
                .container {
                  padding: 0;
                  page-break-inside: avoid;
                  page-break-before: auto;
                  page-break-after: auto;
                }
                .section {
                  box-shadow: none;
                  margin-bottom: 30px;
                  break-inside: avoid;
                }
                .print-button {
                  display: none;
                }
                /* Ensure sections stay together */
                .section + .section {
                  margin-top: 20px;
                  break-before: avoid;
                }
              }
            </style>
          </head>
          <body>
            <button onclick="window.print()" class="print-button">Print</button>
            <div class="container">
              <!-- Email Headers Section -->
              <div class="section">
                <div class="section-header">
                  <h2 class="section-title">Email Headers</h2>
                </div>
                <div class="header-content">
                  <h1 class="subject">${email.envelope.subject || 'No Subject'}</h1>
                  <div class="metadata">
                    <div class="metadata-row">
                      <span class="metadata-label">From:</span>
                      <span class="metadata-value">${email.envelope.from.map(f => f.name || `${f.mailbox}@${f.host}`).join(', ')}</span>
                    </div>
                    <div class="metadata-row">
                      <span class="metadata-label">To:</span>
                      <span class="metadata-value">${email.envelope.to.map(t => t.name || `${t.mailbox}@${t.host}`).join(', ')}</span>
                    </div>
                    ${email.envelope.cc?.length ? `
                    <div class="metadata-row">
                      <span class="metadata-label">Cc:</span>
                      <span class="metadata-value">${email.envelope.cc.map(c => c.name || `${c.mailbox}@${c.host}`).join(', ')}</span>
                    </div>` : ''}
                    <div class="metadata-row">
                      <span class="metadata-label">Date:</span>
                      <span class="metadata-value">${new Date(email.envelope.date).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Email Content Section -->
              <div class="section">
                <div class="section-header">
                  <h2 class="section-title">Email Content</h2>
                </div>
                <div class="email-content">
                  ${getRenderableBody(email.body).html || 'No content available'}
                </div>
              </div>
            </div>
          </body>
        </html>
      `);

      // Wait for content to load
      printWindow.document.close();
      
      // Show success message and print
      if (printToastId.current) {
        toast.success('Email ready to print', {
          id: printToastId.current
        });
      }

      // Trigger print dialog
      printWindow.print();
      
      // Close the window after printing
      printWindow.onafterprint = () => {
        printWindow.close();
      };

    } catch (error) {
      console.error('Error preparing email for print:', error);
      
      // Don't show another error message if we already showed the popup blocked message
      if (error instanceof Error && error.message === 'POPUP_BLOCKED') {
        return;
      }
      
      if (printToastId.current) {
        toast.error('Failed to prepare email for printing. Please try again.', {
          id: printToastId.current,
          duration: 4000
        });
      }
    } finally {
      setIsPrinting(false);
    }
  };

  const handleDownloadSource = async () => {
    if (!email?.id || isDownloading) return;
    
    setIsDownloading(true);
    downloadToastId.current = toast.loading('Downloading Email Source: 0%');

    try {
      const response = await api.get(`/api/email/download/${email.id}`, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (downloadToastId.current) {
              toast.loading(`Downloading Email Source: ${progress}%`, {
                id: downloadToastId.current
              });
            }
          }
        }
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'message/rfc822' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = email.envelope.subject ? `${email.envelope.subject.replace(/[^a-zA-Z0-9]/g, '_')}-${email.id}.eml` : `email_${email.id}.eml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success toast
      if (downloadToastId.current) {
        toast.success('Email Source Downloaded Successfully', {
          id: downloadToastId.current
        });
      }
    } catch (error) {
      console.error('Error downloading email source:', error);
      if (downloadToastId.current) {
        toast.error('Failed to download email source', {
          id: downloadToastId.current
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Update the click outside handler to prevent immediate closing
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showMoreActions && !target.closest('.more-actions-dropdown')) {
        setShowMoreActions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMoreActions]);

  if (!email) {
    if (loadError) {
      return (
        <div style={{ padding: 48, textAlign: 'center', color: '#6b7280' }}>
          <p style={{ marginBottom: 12, color: '#dc2626' }}>{loadError}</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            Go back
          </button>
        </div>
      );
    }
    return <LoadingState folderName={folderPath || 'Email'} />;
  }

  const sender = email.envelope?.from?.[0] || {
    name: 'Unknown',
    mailbox: 'unknown',
    host: 'unknown',
  };
  const senderName = sender.name || `${sender.mailbox}@${sender.host}`;
  const initials = getInitials(senderName);
  const avatarColor = getAvatarColor(senderName);

  // Render reply modal/fullscreen
  const renderReplyForm = () => {
    const content = (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
        height: '100%',
        gap: '0px'
      }}>
        {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          position: 'sticky',
                      top: 0,
          zIndex: 30
        }}>
          <h2 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>
            {replyAll ? 'Reply All' : 'Reply'}
          </h2>
          <button
            onClick={handleCancelReply}
            style={{
              padding: '8px', 
              borderRadius: '6px',
              backgroundColor: 'transparent',
              color: '#6b7280',
              border: 'none',
              cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X className="w-5 h-5" />
          </button>
            </div>

        {/* Content */}
              <div style={{
          flex: 1,
          overflow: 'auto',
          padding: isMobile ? '16px' : '24px',
          paddingBottom: isMobile ? '120px' : '24px', // Increased padding bottom for mobile
          position: 'relative'
        }}>
          {/* Recipients */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
            gap: '16px',
            position: 'relative',
              backgroundColor: '#ffffff',
            zIndex: 20
          }}>
            {/* To Field */}
            <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <label style={{ minWidth: '32px', fontWeight: '600', marginTop: '8px' }}>To:</label>
                  <div style={{ 
                    flex: 1, 
                  minHeight: '24px',
                  padding: '4px',
                  borderBottom: '1px solid #e5e7eb',
                  // borderRadius: '6px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                  // backgroundColor: '#ffffff'
                    }}>
                      {renderRecipientTags('reply-to', toTags, setToTags)}
                    <input 
                      type="text" 
                      placeholder="Add recipient or paste a list" 
                      style={{ 
                        border: 'none',
                        outline: 'none',
                      height: '24px',
                      flex: 1,
                        minWidth: '120px',
                      fontSize: '12px'
                    }}
                    value={activeInput === 'to' ? searchQuery : ''}
                    onChange={e => {
                      setSearchQuery(e.target.value);
                      setActiveInput('to');
                    }}
                    onKeyDown={e => handleKeyDown(e, 'to')}
                    onPaste={(e) => handleRecipientPaste(e, 'to')}
                    onBlur={e => {
                      setTimeout(() => {
                        setShowSuggestions(false);
                        if (handleAddEmailTag(e.target.value, 'to')) {
                          setSearchQuery('');
                        }
                      }, 200);
                    }}
                    onFocus={() => setActiveInput('to')}
                    />
                  </div>
              </div>
              {activeInput === 'to' && showSuggestions && suggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '40px',
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  marginTop: '4px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 50
                }}>
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={suggestion}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: index === activeSuggestionIndex ? '#f3f4f6' : 'transparent'
                      }}
                      onMouseEnter={() => setActiveSuggestionIndex(index)}
                      onMouseDown={() => handleSelectSuggestion(suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* CC/BCC buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              position: 'relative',
              maxHeight: '24px',
              marginBottom: '10px',
              zIndex: 20
            }}>
              {!showCc && (
                    <button
                  onClick={() => setShowCc(true)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                    color: '#6b7280',
                    background: '#f3f4f6',
                        border: 'none',
                    borderRadius: '4px',
                    // marginBottom: '15px',
                    cursor: 'pointer'
                  }}
                >
                  CC
          </button>
              )}
              {!showBcc && (
                    <button
                  onClick={() => setShowBcc(true)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                    color: '#6b7280', 
                    background: '#f3f4f6',
                    // marginBottom: '15px',
                        border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  BCC
                </button>
              )}
                </div>

                {/* CC Field */}
                {showCc && (
                    <div style={{ 
                position: 'relative',
                      backgroundColor: '#ffffff', 
                zIndex: 20
                    }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <label style={{ minWidth: '32px', fontWeight: '600', marginTop: '8px' }}>Cc:</label>
                      <div style={{
                    flex: 1,
                    minHeight: '24px',
                    padding: '4px',
                    marginBottom: '15px',
                    borderBottom: '1px solid #e5e7eb',
                    // borderRadius: '6px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                    backgroundColor: '#ffffff'
                      }}>
                        {renderRecipientTags('reply-cc', ccTags, setCcTags)}
                      <input 
                        type="text" 
                        placeholder="Add CC or paste a list" 
                        style={{ 
                          border: 'none',
                          outline: 'none',
                        flex: 1,
                          minWidth: '120px',
                        fontSize: '12px',
                        height: '24px',
                      }}
                      value={activeInput === 'cc' ? searchQuery : ''}
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        setActiveInput('cc');
                      }}
                      onKeyDown={e => handleKeyDown(e, 'cc')}
                      onPaste={(e) => handleRecipientPaste(e, 'cc')}
                      onBlur={e => {
                        setTimeout(() => {
                          setShowSuggestions(false);
                          if (handleAddEmailTag(e.target.value, 'cc')) {
                            setSearchQuery('');
                          }
                        }, 200);
                      }}
                      onFocus={() => setActiveInput('cc')}
                      />
                    </div>
            <button
                    onClick={() => {
                      setShowCc(false);
                      setCcTags([]);
                    }}
              style={{
                      border: 'none',
                      background: 'none',
                      padding: '8px',
                      marginTop: '4px',
                      cursor: 'pointer',
                      color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
                {activeInput === 'cc' && showSuggestions && suggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '40px',
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    marginTop: '4px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    zIndex: 50
                  }}>
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={suggestion}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: index === activeSuggestionIndex ? '#f3f4f6' : 'transparent'
                        }}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                        onMouseDown={() => handleSelectSuggestion(suggestion)}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
                  </div>
                )}

                {/* BCC Field */}
                {showBcc && (
                    <div style={{ 
                position: 'relative',
                      backgroundColor: '#ffffff', 
                zIndex: 20
                    }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <label style={{ minWidth: '32px', fontWeight: '600', marginTop: '8px' }}>Bcc:</label>
                      <div style={{
                    flex: 1,
                    minHeight: '40px',
                    padding: '8px',
                    borderBottom: '1px solid #e5e7eb',
                    // borderRadius: '6px',
                    marginBottom: '15px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                    backgroundColor: '#ffffff'
                      }}>
                        {renderRecipientTags('reply-bcc', bccTags, setBccTags)}
                      <input 
                        type="text" 
                        placeholder="Add BCC or paste a list" 
                        style={{ 
                          border: 'none',
                          outline: 'none',
                        flex: 1,
                          minWidth: '120px',
                        fontSize: '12px',
                        height: '24px',
                      }}
                      value={activeInput === 'bcc' ? searchQuery : ''}
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        setActiveInput('bcc');
                      }}
                      onKeyDown={e => handleKeyDown(e, 'bcc')}
                      onPaste={(e) => handleRecipientPaste(e, 'bcc')}
                      onBlur={e => {
                        setTimeout(() => {
                          setShowSuggestions(false);
                          if (handleAddEmailTag(e.target.value, 'bcc')) {
                            setSearchQuery('');
                          }
                        }, 200);
                      }}
                      onFocus={() => setActiveInput('bcc')}
                      />
                    </div>
                  <button
                    onClick={() => {
                      setShowBcc(false);
                      setBccTags([]);
                    }}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: '8px',
                      marginTop: '4px',
                      cursor: 'pointer',
                      marginBottom: '15px',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={14} />
            </button>
                </div>
                {activeInput === 'bcc' && showSuggestions && suggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '40px',
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    marginTop: '4px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    zIndex: 50
                  }}>
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={suggestion}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: index === activeSuggestionIndex ? '#f3f4f6' : 'transparent'
                        }}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                        onMouseDown={() => handleSelectSuggestion(suggestion)}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Subject */}
          <div style={{ marginBottom: '16px', marginTop: '4px' }}>
                  <input
                    type="text"
                    value={replySubject}
                    onChange={e => setReplySubject(e.target.value)}
              placeholder="Subject"
                    style={{
                      width: '100%',
                padding: '8px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
              </div>

          {/* Message Editor */}
                <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
                  overflow: 'hidden',
            backgroundColor: '#ffffff'
          }}>
                  <ReactQuill
              ref={quillRef}
                    value={replyBody}
                    onChange={setReplyBody}
              modules={modules}
              formats={formats}
                    theme="snow"
              preserveWhitespace
                    style={{
                height: isMobile ? 'calc(100vh - 400px)' : '300px',
                display: 'flex',
                flexDirection: 'column'
                    }}
                  />
              </div>

          {/* Attachments */}
                <div style={{
            marginTop: '16px',
            marginBottom: isMobile ? '80px' : '16px', // Added margin bottom for mobile
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <button
                onClick={() => forwardFileInputRef.current?.click()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                  color: '#374151',
                      fontSize: '14px',
                  cursor: 'pointer'
                    }}
                  >
                    <Paperclip size={16} />
                    Attach Files
                  </button>
                  <input
                ref={forwardFileInputRef}
                    type="file"
                    multiple
                onChange={handleForwardFileAttach}
                    style={{ display: 'none' }}
                  />
                </div>
            {forwardAttachments.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                {forwardAttachments.map((file, index) => (
                  <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          backgroundColor: '#ffffff',
                          borderRadius: '4px',
                          border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FileText size={16} color="#6b7280" />
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '500' }}>{file.name}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{formatFileSize(file.size)}</div>
                          </div>
                        </div>
                        <button
                      onClick={() => removeForwardAttachment(index)}
                          style={{
                            border: 'none',
                        background: 'none',
                            padding: '4px',
                        color: '#6b7280',
                        cursor: 'pointer'
                          }}
                        >
                      <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

        {/* Footer */}
            <div style={{
          padding: '12px 16px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
          backgroundColor: '#ffffff',
          position: isMobile ? 'fixed' : 'static',
          bottom: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          right: isMobile ? 0 : 'auto',
          zIndex: 30,
          boxShadow: isMobile ? '0 -2px 4px rgba(0, 0, 0, 0.1)' : 'none'
            }}>
              <button
                onClick={handleCancelReply}
                style={{
                  padding: '8px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              background: '#f3f4f6',
                  color: '#374151',
              fontSize: '14px',
              fontWeight: '500'
            }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendReply}
                disabled={isSending}
                style={{
                  padding: '8px 16px',
                  border: 'none',
              borderRadius: '6px',
              background: '#8D00E4',
              color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
              fontWeight: '500',
              fontSize: '14px',
                }}
              >
                {isSending ? (
                  <>
                <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
              'Reply'
                )}
              </button>
            </div>
          </div>
    );

    if (isMobile) {
      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#ffffff',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {content}
        </div>
      );
    }

    return (
      <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
            display: 'flex',
                  alignItems: 'center',
            justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
              backgroundColor: '#ffffff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '800px',
          height: '80vh',
              display: 'flex',
              flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {content}
        </div>
      </div>
    );
  };

  // Forward form with mobile view
  const renderForwardForm = () => {
    const content = (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%',
        gap: '0px'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          position: 'sticky',
          top: 0,
          zIndex: 30
        }}>
          <h2 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>Forward</h2>
          <button 
            onClick={handleCancelForward}
            style={{ 
              padding: '8px', 
              borderRadius: '6px',
              backgroundColor: 'transparent',
              color: '#6b7280',
                  border: 'none',
                  cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
                }}
              >
            <X className="w-5 h-5" />
              </button>
        </div>

        {/* Content */}
                  <div style={{ 
                    flex: 1, 
          overflow: 'auto',
          padding: isMobile ? '16px' : '24px',
          paddingBottom: isMobile ? '120px' : '24px', // Increased padding bottom for mobile
          position: 'relative'
        }}>
          {/* Recipients */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '16px',
            position: 'relative',
                    backgroundColor: '#ffffff', 
            zIndex: 20
                  }}>
            {/* To Field */}
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <label style={{ minWidth: '32px', fontWeight: '600', marginTop: '8px' }}>To:</label>
                    <div style={{
                  flex: 1,
                  minHeight: '24px',
                  padding: '4px',
                  borderBottom: '1px solid #e5e7eb',
                  // borderRadius: '6px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                  // backgroundColor: '#ffffff'
                    }}>
                      {renderRecipientTags('forward-to', forwardToTags, setForwardToTags)}
                    <input 
                      type="text" 
                      placeholder="Add recipient or paste a list" 
                      style={{ 
                        border: 'none',
                        outline: 'none',
                      flex: 1,
                        minWidth: '120px',
                      height: '24px',
                      fontSize: '12px'
                    }}
                    value={activeInput === 'forwardTo' ? searchQuery : ''}
                    onChange={e => {
                      setSearchQuery(e.target.value);
                      setActiveInput('forwardTo');
                    }}
                    onKeyDown={e => handleKeyDown(e, 'forwardTo')}
                    onPaste={(e) => handleRecipientPaste(e, 'forwardTo')}
                    onBlur={e => {
                      setTimeout(() => {
                        setShowSuggestions(false);
                        if (handleAddEmailTag(e.target.value, 'forwardTo')) {
                          setSearchQuery('');
                        }
                      }, 200);
                    }}
                    onFocus={() => setActiveInput('forwardTo')}
                    />
                  </div>
              </div>
              {activeInput === 'forwardTo' && showSuggestions && suggestions.length > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                  left: '40px',
                    right: 0,
                  backgroundColor: 'white',
                  borderBottom: '1px solid #e5e7eb',
                  // borderRadius: '6px',
                  marginTop: '4px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 50
                }}>
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={suggestion}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: index === activeSuggestionIndex ? '#f3f4f6' : 'transparent'
                      }}
                      onMouseEnter={() => setActiveSuggestionIndex(index)}
                      onMouseDown={() => handleSelectSuggestion(suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* CC/BCC buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              marginTop: '0px',
              position: 'relative',
              zIndex: 20
            }}>
              {!showForwardCc && (
                    <button
                  onClick={() => setShowForwardCc(true)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                    color: '#6b7280',
                    background: '#f3f4f6',
                        border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  CC
                </button>
              )}
              {!showForwardBcc && (
                    <button
                  onClick={() => setShowForwardBcc(true)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                    color: '#6b7280',
                    background: '#f3f4f6',
                        border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  BCC
                </button>
              )}
                </div>

                {/* CC Field */}
                {showForwardCc && (
                    <div style={{ 
                position: 'relative',
                      backgroundColor: '#ffffff', 
                zIndex: 20
                    }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <label style={{ minWidth: '32px', fontWeight: '600', marginTop: '8px' }}>Cc:</label>
                      <div style={{
                    flex: 1,
                    minHeight: '24px',
                    padding: '4px',
                    borderBottom: '1px solid #e5e7eb',
                    // borderRadius: '6px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                    // backgroundColor: '#ffffff'
                      }}>
                        {renderRecipientTags('forward-cc', forwardCcTags, setForwardCcTags)}
                      <input 
                        type="text" 
                        placeholder="Add CC or paste a list" 
                        style={{ 
                          border: 'none',
                          outline: 'none',
                        flex: 1,
                          minWidth: '120px',
                        height: '24px',
                        fontSize: '12px'
                      }}
                      value={activeInput === 'forwardCc' ? searchQuery : ''}
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        setActiveInput('forwardCc');
                      }}
                      onKeyDown={e => handleKeyDown(e, 'forwardCc')}
                      onPaste={(e) => handleRecipientPaste(e, 'forwardCc')}
                      onBlur={e => {
                        setTimeout(() => {
                          setShowSuggestions(false);
                          if (handleAddEmailTag(e.target.value, 'forwardCc')) {
                            setSearchQuery('');
                          }
                        }, 200);
                      }}
                      onFocus={() => setActiveInput('forwardCc')}
                      />
                    </div>
                  <button
                    onClick={() => {
                      setShowForwardCc(false);
                      setForwardCcTags([]);
                    }}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: '8px',
                      marginTop: '4px',
                      cursor: 'pointer',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
                {activeInput === 'forwardCc' && showSuggestions && suggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '40px',
                    right: 0,
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    // borderRadius: '6px',
                    marginTop: '4px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    zIndex: 50
                  }}>
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={suggestion}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: index === activeSuggestionIndex ? '#f3f4f6' : 'transparent'
                        }}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                        onMouseDown={() => handleSelectSuggestion(suggestion)}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
                  </div>
                )}

                {/* BCC Field */}
                {showForwardBcc && (
                    <div style={{ 
                position: 'relative',
                      backgroundColor: '#ffffff', 
                zIndex: 20
                    }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <label style={{ minWidth: '32px', fontWeight: '600', marginTop: '8px' }}>Bcc:</label>
                      <div style={{
                    flex: 1,
                    minHeight: '24px',
                    padding: '4px',
                    borderBottom: '1px solid #e5e7eb',
                    // borderRadius: '6px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                    // backgroundColor: '#ffffff'
                      }}>
                        {renderRecipientTags('forward-bcc', forwardBccTags, setForwardBccTags)}
                      <input 
                        type="text" 
                        placeholder="Add BCC or paste a list" 
                        style={{ 
                          border: 'none',
                          outline: 'none',
                        flex: 1,
                          minWidth: '120px',
                        height: '24px',
                        fontSize: '12px'
                      }}
                      value={activeInput === 'forwardBcc' ? searchQuery : ''}
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        setActiveInput('forwardBcc');
                      }}
                      onKeyDown={e => handleKeyDown(e, 'forwardBcc')}
                      onPaste={(e) => handleRecipientPaste(e, 'forwardBcc')}
                      onBlur={e => {
                        setTimeout(() => {
                          setShowSuggestions(false);
                          if (handleAddEmailTag(e.target.value, 'forwardBcc')) {
                            setSearchQuery('');
                          }
                        }, 200);
                      }}
                      onFocus={() => setActiveInput('forwardBcc')}
                      />
                    </div>
                  <button
                    onClick={() => {
                      setShowForwardBcc(false);
                      setForwardBccTags([]);
                    }}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: '8px',
                      marginTop: '4px',
                      cursor: 'pointer',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
                {activeInput === 'forwardBcc' && showSuggestions && suggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '40px',
                    right: 0,
                    backgroundColor: 'white',
                      borderBottom: '1px solid #e5e7eb',
                    // borderRadius: '6px',
                    marginTop: '4px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    zIndex: 50
                  }}>
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={suggestion}
                        style={{
                          padding: '8px 12px',
                      cursor: 'pointer',
                          backgroundColor: index === activeSuggestionIndex ? '#f3f4f6' : 'transparent'
                        }}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                        onMouseDown={() => handleSelectSuggestion(suggestion)}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Subject */}
          <div style={{ marginTop: '16px' }}>
                  <input
                    type="text"
                    value={forwardSubject}
                    onChange={e => setForwardSubject(e.target.value)}
              placeholder="Subject"
                    style={{
                      width: '100%',
                padding: '8px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                      fontSize: '14px',
                backgroundColor: '#ffffff'
              }}
            />
              </div>

          {/* Message Editor */}
                <div style={{
            marginTop: '16px',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
                  overflow: 'hidden',
            backgroundColor: '#ffffff'
          }}>
                  <ReactQuill
              ref={quillRef}
                    value={forwardBody}
                    onChange={setForwardBody}
              modules={modules}
              formats={formats}
                    theme="snow"
              preserveWhitespace
                    style={{
                height: isMobile ? 'calc(100vh - 400px)' : '300px',
                display: 'flex',
                flexDirection: 'column'
                    }}
                  />
              </div>

          {/* Attachments */}
                <div style={{
            marginTop: '16px',
            marginBottom: isMobile ? '80px' : '16px', // Added margin bottom for mobile
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <button
                    onClick={() => forwardFileInputRef.current?.click()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      color: '#374151',
                      fontSize: '14px',
                  cursor: 'pointer'
                    }}
                  >
                    <Paperclip size={16} />
                    Attach Files
                  </button>
                  <input
                    ref={forwardFileInputRef}
                    type="file"
                    multiple
                    onChange={handleForwardFileAttach}
                    style={{ display: 'none' }}
                  />
                </div>
                {forwardAttachments.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    {forwardAttachments.map((file, index) => (
                  <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          backgroundColor: '#ffffff',
                          borderRadius: '4px',
                          border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileText size={16} color="#6b7280" />
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '500' }}>{file.name}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{formatFileSize(file.size)}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeForwardAttachment(index)}
                      style={{
                        border: 'none',
                        background: 'none',
                        padding: '4px',
                        color: '#6b7280',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={16} />
                  </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
                        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: '#ffffff',
          position: isMobile ? 'fixed' : 'static',
          bottom: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          right: isMobile ? 0 : 'auto',
          zIndex: 30,
          boxShadow: isMobile ? '0 -2px 4px rgba(0, 0, 0, 0.1)' : 'none'
        }}>
          <button
            onClick={handleCancelForward}
            style={{
              padding: '8px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              background: '#f3f4f6',
              color: '#374151',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSendForward}
            disabled={isForwarding}
                    style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              background: '#8D00E4',
              color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
              fontWeight: '500',
                      fontSize: '14px',
            }}
          >
            {isForwarding ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send'
            )}
                  </button>
                </div>
            </div>
    );

    if (isMobile) {
      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#ffffff',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {content}
          </div>
      );
    }

    return (
                          <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '800px',
          height: '80vh',
                            display: 'flex',
                            flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {content}
        </div>
      </div>
    );
  };

  // Quill configuration is now moved to the top of the file

  // Function to resize iframe based on content
  const resizeIframe = useCallback(() => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const height = iframeRef.current.contentWindow.document.documentElement.scrollHeight;
      iframeRef.current.style.height = `${height}px`;
    }
  }, []);

  return (
    <div className="gmail-view-wrapper">
      <EmailViewPanel
        email={email}
        isMobile={isMobile}
        isStarred={isStarred}
        senderName={senderName}
        sender={sender}
        initials={initials}
        avatarColor={avatarColor}
        showMoreActions={showMoreActions}
        setShowMoreActions={setShowMoreActions}
        isSendingCopy={isSendingCopy}
        isShowingOriginal={isShowingOriginal}
        isViewing={isViewing}
        isPrinting={isPrinting}
        isDownloading={isDownloading}
        replies={replies}
        repliesLoading={repliesLoading}
        hasMore={hasMore}
        loadMore={loadMore}
        iframeRef={iframeRef}
        formatDate={formatDate}
        formatFileSize={formatFileSize}
        resizeIframe={resizeIframe}
        onBack={handleBack}
        onReply={handleReply}
        onReplyAll={handleReplyAll}
        onForward={handleForward}
        onStar={handleStar}
        onDelete={handleDelete}
        onSendCopy={handleSendCopy}
        onShowOriginal={handleShowOriginal}
        onViewHtml={handleViewHtml}
        onPrint={handlePrintEmail}
        onDownload={handleDownloadSource}
        onAttachmentDownload={handleAttachmentDownload}
        fetchAttachmentBlob={fetchAttachmentBlob}
        bodyFallback={latestReplyFallbackRef.current || undefined}
        totalReplies={replies.length || effectiveReplyCount || 0}
      />

      {showReply && renderReplyForm()}
      {showForward && renderForwardForm()}
    </div>
  );
};

export default EmailChatPage; 